/**
 * IndustryEditorModal — the 4-tab create/edit modal for an industry
 * profile. Edits identity (Basics), KPI baselines, the LEI × KPI
 * weight + elasticity matrix, and the aggregate caps / AI tone block.
 * Each tab opens with a collapsible TabGuidance panel; each numeric
 * input renders peer-stat hints so admins see the active library
 * baseline before drifting outside it.
 */
import React from 'react';
import {
  Plus, Trash2, Save, X, Sparkles, Loader2, AlertCircle,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { ICON_CHOICES, slugify, peerStats } from './helpers';
import { TabGuidance, PeerHint } from './TabGuidance';

export default function IndustryEditorModal({
  draft, setDraft, isCreate, saving, onCancel, onSave, metadata,
  initialTab = 'basics', peerProfiles = [],
}) {
  const kpis = metadata?.kpis || [];
  const measures = metadata?.measures || [];
  const globalWeights = metadata?.lei_kpi_weights || {};
  const globalDampening = metadata?.portfolio_dampening_factor;
  const globalMaxUplift = metadata?.max_kpi_uplift_percent;

  const patch = (changes) => setDraft((d) => ({ ...d, ...changes }));

  // ── KPI baselines helpers
  const addBaseline = () => {
    patch({ kpi_baselines: [...(draft.kpi_baselines || []), { kpi_id: '', baseline_per_employee: '', source: '' }] });
  };
  const updateBaseline = (idx, field, value) => {
    const arr = [...(draft.kpi_baselines || [])];
    arr[idx] = { ...arr[idx], [field]: value };
    patch({ kpi_baselines: arr });
  };
  const removeBaseline = (idx) => {
    patch({ kpi_baselines: (draft.kpi_baselines || []).filter((_, i) => i !== idx) });
  };

  // ── LEI×KPI weights helpers
  const addWeight = () => {
    patch({ lei_kpi_weights: [...(draft.lei_kpi_weights || []), { measure_id: '', kpi_id: '', weight: '' }] });
  };
  const updateWeight = (idx, field, value) => {
    const arr = [...(draft.lei_kpi_weights || [])];
    arr[idx] = { ...arr[idx], [field]: value };
    patch({ lei_kpi_weights: arr });
  };
  const removeWeight = (idx) => {
    patch({ lei_kpi_weights: (draft.lei_kpi_weights || []).filter((_, i) => i !== idx) });
  };

  // ── Elasticities helpers
  const addElasticity = () => {
    patch({ elasticities: [...(draft.elasticities || []), { measure_id: '', elasticity_moderate: '' }] });
  };
  const updateElasticity = (idx, field, value) => {
    const arr = [...(draft.elasticities || [])];
    arr[idx] = { ...arr[idx], [field]: value };
    patch({ elasticities: arr });
  };
  const removeElasticity = (idx) => {
    patch({ elasticities: (draft.elasticities || []).filter((_, i) => i !== idx) });
  };

  // ── KPI priorities (comma-separated input, normalized list)
  const prioritiesText = (draft.kpi_priorities || []).join(', ');
  const setPriorities = (text) => {
    const list = text
      .split(/[,\s]+/)
      .map((s) => s.trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_'))
      .filter(Boolean);
    patch({ kpi_priorities: list });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-xl bg-white shadow-2xl flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              {isCreate ? 'New industry profile' : `Edit · ${draft.name || draft.id}`}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Coefficients below override global defaults for any org assigned to this industry.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            data-testid="industry-editor-close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="overflow-y-auto px-6 py-4 flex-1">
          <Tabs defaultValue={initialTab} className="w-full">
            <TabsList className="grid grid-cols-4 mb-4" data-testid="industry-editor-tabs">
              <TabsTrigger value="basics" data-testid="tab-basics">Basics</TabsTrigger>
              <TabsTrigger value="kpis" data-testid="tab-kpis">KPI baselines</TabsTrigger>
              <TabsTrigger value="matrix" data-testid="tab-matrix">LEI × KPI matrix</TabsTrigger>
              <TabsTrigger value="caps" data-testid="tab-caps">Caps & AI tone</TabsTrigger>
            </TabsList>

            {/* ── Basics ─────────────────────────────────────────── */}
            <TabsContent value="basics" className="space-y-4">
              <TabGuidance
                testId="guidance-basics"
                title="What goes here"
                what="Identity for this industry profile — slug + name + KPI priorities. The slug is permanent; the KPI priorities determine which KPIs headline the prospectus."
                affects="Slug ties orgs to this profile (via settings.industry_id) and appears in the resolver chain. KPI priorities feed the top-3 chip rendered everywhere this industry is shown."
                realistic="Slug: 2–4 words snake_case. Priorities: list 3–5 KPIs that *this industry* lives or dies by — not all 10. E.g. Healthcare leads with quality + safety; Tech leads with speed_to_market + productivity."
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Slug (id)</Label>
                  <Input
                    value={draft.id}
                    disabled={!isCreate}
                    onChange={(e) => patch({ id: slugify(e.target.value) })}
                    placeholder="logistics"
                    data-testid="industry-editor-id"
                    className="font-mono"
                  />
                  {isCreate && (
                    <p className="text-[10px] text-slate-500 mt-1">
                      Lowercase, snake_case. Used in the URL and resolver.
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs">Short label</Label>
                  <Input
                    value={draft.short_label}
                    onChange={(e) => patch({ short_label: e.target.value })}
                    placeholder="Logistics"
                    data-testid="industry-editor-short-label"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Display name</Label>
                <Input
                  value={draft.name}
                  onChange={(e) => patch({ name: e.target.value })}
                  placeholder="Logistics & Supply Chain"
                  data-testid="industry-editor-name"
                />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Textarea
                  value={draft.description}
                  onChange={(e) => patch({ description: e.target.value })}
                  placeholder="What sets this industry apart for ROI modelling…"
                  rows={2}
                  data-testid="industry-editor-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Icon</Label>
                  <Select value={draft.icon || 'Layers'} onValueChange={(v) => patch({ icon: v })}>
                    <SelectTrigger data-testid="industry-editor-icon">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ICON_CHOICES.map((name) => (
                        <SelectItem key={name} value={name}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select
                    value={draft.published === false ? 'archived' : 'published'}
                    onValueChange={(v) => patch({ published: v === 'published' })}
                  >
                    <SelectTrigger data-testid="industry-editor-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="archived">Archived</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Headline KPI priorities</Label>
                <Input
                  value={prioritiesText}
                  onChange={(e) => setPriorities(e.target.value)}
                  placeholder="safety, quality, productivity, …"
                  data-testid="industry-editor-priorities"
                  className="font-mono"
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Comma-separated KPI ids in priority order. First 3 power the prospectus headline panel.
                </p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {(draft.kpi_priorities || []).map((kid, i) => (
                    <span
                      key={kid + i}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800"
                    >
                      <span className="text-indigo-400">#{i + 1}</span>
                      {kid.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* ── KPI baselines ──────────────────────────────────── */}
            <TabsContent value="kpis" className="space-y-3">
              <TabGuidance
                testId="guidance-kpis"
                title="KPI baselines (dollars per employee per year)"
                what="The annual financial value, per employee, that each KPI represents for this industry. Drives ROI math: financial_impact = baseline × headcount × (kpi_delta_percent / 100)."
                affects="Single-measure / Portfolio / Full-matrix ROI calculations, all prospectus headline numbers, AI Coach financial framing. Missing rows inherit the global default for that KPI."
                realistic={'Typical: safety $2.5k (manufacturing $4.5k), quality $3.5k (healthcare $7k), productivity $5k (tech $7.5k), speed_to_market $8k (tech $11k). If you set a value 3× the peer max, downstream ROI charts will look implausibly large — admins should match published industry benchmarks (e.g. ATD State of the Industry).'}
              />
              <div className="text-xs text-slate-500">
                Override $/employee/yr baselines per KPI. Leave a KPI out to inherit the global default.
              </div>
              <div className="space-y-2" data-testid="kpi-baselines-list">
                {(draft.kpi_baselines || []).map((b, idx) => {
                  const kpi = kpis.find((k) => k.id === b.kpi_id);
                  const peer = b.kpi_id
                    ? peerStats(peerProfiles, draft.id, (p) => {
                        const row = (p.kpi_baselines || []).find((x) => x.kpi_id === b.kpi_id);
                        return row ? row.baseline_per_employee : null;
                      })
                    : null;
                  return (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-start rounded-lg border border-slate-200 p-2">
                      <div className="col-span-4">
                        <Select value={b.kpi_id} onValueChange={(v) => updateBaseline(idx, 'kpi_id', v)}>
                          <SelectTrigger data-testid={`kpi-baseline-kpi-${idx}`} className="h-8 text-xs">
                            <SelectValue placeholder="Pick KPI…" />
                          </SelectTrigger>
                          <SelectContent>
                            {kpis.map((k) => (
                              <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {kpi && (
                          <p className="text-[10px] text-slate-400 mt-1 font-mono">
                            global ${kpi.baseline_per_employee.toLocaleString()}
                          </p>
                        )}
                      </div>
                      <div className="col-span-3">
                        <Input
                          type="number"
                          value={b.baseline_per_employee}
                          onChange={(e) => updateBaseline(idx, 'baseline_per_employee', e.target.value)}
                          placeholder="$ /emp/yr"
                          className="h-8 text-xs font-mono"
                          data-testid={`kpi-baseline-value-${idx}`}
                        />
                        <PeerHint
                          stats={peer}
                          currentValue={b.baseline_per_employee}
                          prefix="$"
                          testId={`kpi-baseline-peer-${idx}`}
                        />
                      </div>
                      <div className="col-span-4">
                        <Input
                          value={b.source || ''}
                          onChange={(e) => updateBaseline(idx, 'source', e.target.value)}
                          placeholder="Source / citation (optional)"
                          className="h-8 text-xs"
                          data-testid={`kpi-baseline-source-${idx}`}
                        />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <Button variant="ghost" size="sm" onClick={() => removeBaseline(idx)} data-testid={`kpi-baseline-remove-${idx}`}>
                          <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Button variant="outline" size="sm" onClick={addBaseline} data-testid="kpi-baseline-add">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add KPI baseline
              </Button>
            </TabsContent>

            {/* ── LEI × KPI weights + elasticities ───────────────── */}
            <TabsContent value="matrix" className="space-y-3">
              <TabGuidance
                testId="guidance-matrix"
                title="LEI × KPI weights & elasticities"
                what="Two coefficient sets. (1) Weights: 0–1 scalar saying how strongly a learning measure pulls a specific KPI. (2) Elasticities: 0–2 scalar saying how much a KPI moves per 1 unit of measure improvement in this industry."
                affects="The core of the ROI math. Both flow into kpi_delta = improvement × weight × elasticity × dampening × maturity. Tuning these lets one industry's training_performance measure punch harder on safety than another's."
                realistic="Weights: most cells are 0.3–0.7; >0.85 = strong direct dependency (e.g. certification_depth × compliance). Elasticities: most fall 0.4–0.8 globally; healthcare safety elasticity 0.85, tech speed_to_market 0.75. Don't push elasticities >1.2 unless you have published research backing it — values >1 mean a 1% measure lift produces >1% KPI lift, which is rare outside crisis-response training."
              />
              <div className="text-xs text-slate-500">
                Override individual cells in the 15 × 10 weight matrix. Each cell expresses how strongly a measure influences a KPI (0–1). Unspecified cells inherit the global default.
              </div>
              <div className="space-y-2" data-testid="lei-weights-list">
                {(draft.lei_kpi_weights || []).map((w, idx) => {
                  const globalCell = globalWeights[w.measure_id]?.[w.kpi_id];
                  const peer = (w.measure_id && w.kpi_id)
                    ? peerStats(peerProfiles, draft.id, (p) => {
                        const row = (p.lei_kpi_weights || []).find(
                          (x) => x.measure_id === w.measure_id && x.kpi_id === w.kpi_id,
                        );
                        return row ? row.weight : null;
                      })
                    : null;
                  return (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-start rounded-lg border border-slate-200 p-2">
                      <div className="col-span-5">
                        <Select value={w.measure_id} onValueChange={(v) => updateWeight(idx, 'measure_id', v)}>
                          <SelectTrigger data-testid={`weight-measure-${idx}`} className="h-8 text-xs">
                            <SelectValue placeholder="LEI measure…" />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {measures.map((m) => (
                              <SelectItem key={m.id} value={m.id}>
                                <span className="text-xs">{m.name}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-4">
                        <Select value={w.kpi_id} onValueChange={(v) => updateWeight(idx, 'kpi_id', v)}>
                          <SelectTrigger data-testid={`weight-kpi-${idx}`} className="h-8 text-xs">
                            <SelectValue placeholder="KPI…" />
                          </SelectTrigger>
                          <SelectContent>
                            {kpis.map((k) => (
                              <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          step="0.05"
                          min="0"
                          max="1"
                          value={w.weight}
                          onChange={(e) => updateWeight(idx, 'weight', e.target.value)}
                          placeholder="0.0–1.0"
                          className="h-8 text-xs font-mono"
                          data-testid={`weight-value-${idx}`}
                        />
                        {globalCell != null && (
                          <p className="text-[10px] text-slate-400 mt-1 font-mono">global {globalCell}</p>
                        )}
                        <PeerHint
                          stats={peer}
                          currentValue={w.weight}
                          formatter={(n) => n.toFixed(2)}
                          testId={`weight-peer-${idx}`}
                        />
                      </div>
                      <div className="col-span-1 flex justify-end">
                        <Button variant="ghost" size="sm" onClick={() => removeWeight(idx)} data-testid={`weight-remove-${idx}`}>
                          <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Button variant="outline" size="sm" onClick={addWeight} data-testid="weight-add">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add weight override
              </Button>

              <div className="mt-6 pt-4 border-t border-slate-200">
                <div className="text-xs font-semibold text-slate-700 mb-2">Elasticity overrides</div>
                <div className="text-xs text-slate-500 mb-3">
                  Per-measure elasticity (0–2) overriding the moderate-scenario default.
                </div>
                <div className="space-y-2" data-testid="elasticities-list">
                  {(draft.elasticities || []).map((el, idx) => {
                    const m = measures.find((mm) => mm.id === el.measure_id);
                    const peer = el.measure_id
                      ? peerStats(peerProfiles, draft.id, (p) => {
                          const row = (p.elasticities || []).find((x) => x.measure_id === el.measure_id);
                          return row ? row.elasticity_moderate : null;
                        })
                      : null;
                    return (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-start rounded-lg border border-slate-200 p-2">
                        <div className="col-span-7">
                          <Select value={el.measure_id} onValueChange={(v) => updateElasticity(idx, 'measure_id', v)}>
                            <SelectTrigger data-testid={`elasticity-measure-${idx}`} className="h-8 text-xs">
                              <SelectValue placeholder="LEI measure…" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                              {measures.map((mm) => (
                                <SelectItem key={mm.id} value={mm.id}>{mm.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-4">
                          <Input
                            type="number"
                            step="0.05"
                            min="0"
                            max="2"
                            value={el.elasticity_moderate}
                            onChange={(e) => updateElasticity(idx, 'elasticity_moderate', e.target.value)}
                            placeholder="0.0–2.0"
                            className="h-8 text-xs font-mono"
                            data-testid={`elasticity-value-${idx}`}
                          />
                          {m && (
                            <p className="text-[10px] text-slate-400 mt-1 font-mono">global {m.elasticity_moderate}</p>
                          )}
                          <PeerHint
                            stats={peer}
                            currentValue={el.elasticity_moderate}
                            formatter={(n) => n.toFixed(2)}
                            testId={`elasticity-peer-${idx}`}
                          />
                        </div>
                        <div className="col-span-1 flex justify-end">
                          <Button variant="ghost" size="sm" onClick={() => removeElasticity(idx)} data-testid={`elasticity-remove-${idx}`}>
                            <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <Button variant="outline" size="sm" onClick={addElasticity} data-testid="elasticity-add">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add elasticity override
                </Button>
              </div>
            </TabsContent>

            {/* ── Caps & AI tone ─────────────────────────────────── */}
            <TabsContent value="caps" className="space-y-4">
              <TabGuidance
                testId="guidance-caps"
                title="Aggregate caps & AI Coach voice"
                what="Two safety rails on aggregate ROI math + the plain-English block that gets injected into the AI Coach and Insights system prompts for this industry."
                affects="Dampening = how much the portfolio total shrinks when stacking many measures (prevents claims like '12 interventions → 30% productivity gain'). Max uplift = hard ceiling on per-KPI gain claimed in any one model. LLM context shifts AI tone — 'safety-critical regulated environment' rewrites Coach narratives end-to-end."
                realistic="Dampening: 0.30–0.55. <0.30 = aggressively conservative (good for high-stakes regulated industries). >0.65 = optimistic, only justified when measures are highly orthogonal. Max uplift: 1.5–4%. Most industries land at 2.5–3%. Above 5% requires very strong evidence and will trigger client pushback in audits."
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Portfolio dampening factor</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={draft.portfolio_dampening_factor}
                    onChange={(e) => patch({ portfolio_dampening_factor: e.target.value })}
                    placeholder={globalDampening != null ? `global ${globalDampening}` : '0.0–1.0'}
                    className="font-mono"
                    data-testid="industry-editor-dampening"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    Lower = more conservative ROI uplift across overlapping measures.
                  </p>
                  <PeerHint
                    stats={peerStats(peerProfiles, draft.id, (p) => p.portfolio_dampening_factor)}
                    currentValue={draft.portfolio_dampening_factor}
                    formatter={(n) => n.toFixed(2)}
                    testId="industry-editor-dampening-peer"
                  />
                </div>
                <div>
                  <Label className="text-xs">Max KPI uplift (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    value={draft.max_kpi_uplift_percent}
                    onChange={(e) => patch({ max_kpi_uplift_percent: e.target.value })}
                    placeholder={globalMaxUplift != null ? `global ${globalMaxUplift}` : '%'}
                    className="font-mono"
                    data-testid="industry-editor-max-uplift"
                  />
                  <PeerHint
                    stats={peerStats(peerProfiles, draft.id, (p) => p.max_kpi_uplift_percent)}
                    currentValue={draft.max_kpi_uplift_percent}
                    suffix="%"
                    formatter={(n) => n.toFixed(2)}
                    testId="industry-editor-max-uplift-peer"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs flex items-center gap-1">
                  <Sparkles className="h-3.5 w-3.5 text-amber-500" /> AI Coach / Insights industry context
                </Label>
                <Textarea
                  value={draft.llm_industry_context || ''}
                  onChange={(e) => patch({ llm_industry_context: e.target.value })}
                  placeholder="Plain English description of this industry's language and norms. Fed into Coach + Insights system prompts."
                  rows={5}
                  data-testid="industry-editor-llm-context"
                />
                <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">
                  <strong>Write like a stage direction.</strong> Examples: <em>&quot;Safety-critical regulated environment. Frame impact in incidents avoided, not productivity points. Use clinical, careful language.&quot;</em>  or <em>&quot;High-velocity SaaS. Frame impact as time-to-ship, retention, customer NPS. Be direct and bias to action.&quot;</em> The Coach + Insights LLMs read this verbatim and shift tone accordingly. 2–4 sentences usually beats a paragraph.
                </p>
              </div>
              {isCreate && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    Once created, the slug <code className="font-mono">{slugify(draft.id) || '—'}</code> cannot be changed. Org admins will reference this profile by its slug.
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={saving} data-testid="industry-editor-cancel-bottom">
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="industry-editor-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {isCreate ? 'Create profile' : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
